import { useContext, useState, useEffect, useRef } from "react";
import Slider from "react-slick";
import PlanCard from "./PlanCard";
import styled from "@emotion/styled";
import { DynamicContext } from "../../../Context/DynamicContext";
import { Toast } from "../../../Common/Toast-Snackbar/ToastSnackbar";
import {
  VYLABS_BASE_URL,
  SELECT_FREE_TRIAL_PLAN,
  UPDATE_SUBSCRIPTION_PLAN,
} from "../../../helper/url_helper";
import { post } from "../../../helper/Api_helper";
import { transformApiPlansToCardFormat } from "./PlanHelper";
import { IconButton } from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

function PlansDetails({
  plansData = [],
  isManualBilling = false,
  onPlanSelect,
}) {
  const { isDarkMode, userTimezone } = useContext(DynamicContext);
  const [transformedPlans, setTransformedPlans] = useState([]);
  const [allPlans, setAllPlans] = useState([]);
  const sliderRef = useRef(null);

  const customPlan = {
    id: "custom",
    name: "Custom",
    subHeader: "Tailored solutions for your unique requirements",
    price: "Custom /month",
    features: [
      "Users limit based on requirement",
      "Unlimited resume parser credits",
      "AI Candidate Search",
      "AI Job Matcher",
      "Candidate management",
      "Job pipeline management",
      "Interview scheduling",
      "Email Campaigns",
      "Job Posting",
      "Vendor management",
      "3rd-party API integration",
    ],
    highlighted: false,
    buttonText: "Contact Us",
    trialButtonText: null,
    isCustom: true,
  };

  const sliderSettings = {
    dots: true,
    infinite: false,
    speed: 500,
    slidesToShow: 3,
    slidesToScroll: 1,
    initialSlide: 0,
    arrows: false,
    variableWidth: true,
    centerMode: false,
    responsive: [
      {
        breakpoint: 768,
        settings: {
          variableWidth: false,
          slidesToShow: 1,
          centerMode: false,
        },
      },
    ],
  };

  const StyledSliderContainer = styled.div`
    position: relative;
    padding: 0 40px;
    margin: 20px 0;

    .slick-slider {
      position: relative;
      margin-left: 16px;
    }

    .slick-list {
      margin: 0 -10px;
    }

    .slick-slide > div {
      padding: 0 10px;
    }

    .slick-track {
      display: flex !important;
      gap: 0;
    }

    .slick-slide {
      height: inherit !important;
      & > div {
        height: 100%;
      }
    }

    .slick-dots {
      bottom: -40px;

      li {
        margin: 0 4px;

        button {
          &:before {
            font-size: 10px;
            color: ${({ isDarkMode }) => (isDarkMode ? "#8b5cf6" : "#6c63ff")};
            opacity: 0.5;
          }
        }

        &.slick-active button:before {
          opacity: 1;
          color: ${({ isDarkMode }) => (isDarkMode ? "#8b5cf6" : "#6c63ff")};
        }
      }
    }

    ${({ theme }) => theme.breakpoints.down("sm")} {
      padding: 0 20px;
    }
  `;

  const NavigationButton = styled(IconButton)`
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    width: 40px;
    height: 40px;
    background: ${({ isDarkMode }) => (isDarkMode ? "#4a42d4" : "#6c63ff")};
    color: white;
    z-index: 10;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
    transition: all 0.3s ease;

    &:hover {
      background: ${({ isDarkMode }) => (isDarkMode ? "#7c3aed" : "#4a42d4")};
      transform: translateY(-50%) scale(1.1);
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
      transform: translateY(-50%);
    }
  `;

  const PrevButton = styled(NavigationButton)`
    left: 0;
  `;

  const NextButton = styled(NavigationButton)`
    right: 0;
  `;

  useEffect(() => {
    if (plansData && plansData.length > 0) {
      try {
        const transformed = transformApiPlansToCardFormat(
          plansData,
          userTimezone,
        );
        const sortedPlans = transformed.sort((a, b) => {
          const order = {
            starter: 1,
            professional: 2,
            enterprise: 3,
          };
          const aOrder = order[a.name.toLowerCase()] || 99;
          const bOrder = order[b.name.toLowerCase()] || 99;
          return aOrder - bOrder;
        });
        const allPlansWithCustom = [...sortedPlans, customPlan];

        setTransformedPlans(sortedPlans);
        setAllPlans(allPlansWithCustom);
      } catch (error) {
        console.error("Error transforming plans:", error);
        setTransformedPlans([]);
        setAllPlans([customPlan]);
      }
    } else {
      setAllPlans([customPlan]);
    }
  }, [plansData, userTimezone]);

  const handleStartActivePlanClick = async (planName) => {
    try {
      if (planName.toLowerCase() === "custom") {
        window.location.href = "mailto:dm@vylabs.ai";
        return;
      }
      const response = await post(VYLABS_BASE_URL, UPDATE_SUBSCRIPTION_PLAN);
      if (response && response.portal_session_url) {
        window.location.href = response.portal_session_url;
      } else {
        console.error("No portal session URL received");
        Toast("error", "Failed to get portal session URL");
      }
    } catch (error) {
      console.error("Error updating subscription plan:", error);
      Toast("error", error.message || "An error occurred");
    }
  };

  const handleTrialClick = async (planName) => {
    if (planName.toLowerCase() === "custom") {
      window.location.href = "mailto:dm@vylabs.ai";
      return;
    }
    if (onPlanSelect) {
      onPlanSelect(planName);
      return;
    }
    try {
      const response = await post(VYLABS_BASE_URL, SELECT_FREE_TRIAL_PLAN, {
        plan: planName,
      });

      if (response && response.checkout_session_url) {
        window.location.href = response.checkout_session_url;
      } else {
        console.error("No checkout session URL received");
      }
    } catch (error) {
      console.error("Error selecting plan:", error);
      Toast("error", error);
    }
  };

  const handleNext = () => {
    sliderRef.current.slickNext();
  };

  const handlePrev = () => {
    sliderRef.current.slickPrev();
  };

  const plansToRender = allPlans.length > 0 ? allPlans : transformedPlans;

  return (
    <>
      <StyledSliderContainer isDarkMode={isDarkMode}>
        <PrevButton
          isDarkMode={isDarkMode}
          onClick={handlePrev}
          aria-label="Previous slide"
          size="large"
        >
          <ArrowBackIosNewIcon fontSize="small" />
        </PrevButton>

        <Slider ref={sliderRef} {...sliderSettings}>
          {plansToRender.map((plan, index) => (
            <div key={plan.id || index} style={{ height: "100%" }}>
              <PlanCard
                name={plan.name}
                index={index}
                subHeader={plan.subHeader}
                price={plan.price}
                features={plan.features}
                buttonText={plan.buttonText}
                trialButtonText={plan.trialButtonText}
                highlighted={plan.highlighted}
                isDarkMode={isDarkMode}
                isCustom={plan.isCustom || false}
                isManualBilling={isManualBilling}
                onTrialClick={handleTrialClick || onPlanSelect}
                onPlanClick={handleStartActivePlanClick}
              />
            </div>
          ))}
        </Slider>

        <NextButton
          isDarkMode={isDarkMode}
          onClick={handleNext}
          aria-label="Next slide"
          size="large"
        >
          <ArrowForwardIosIcon fontSize="small" />
        </NextButton>
      </StyledSliderContainer>
    </>
  );
}

export default PlansDetails;